import { useState } from "react";
import { Button, useToast } from "@humansignal/ui";
import { useAPI } from "@humansignal/core";
import { cn } from "../../utils/bem";
import { Modal } from "../Modal/Modal";
import "./FeedbackModal.prefix.css";

const block = cn("feedback-modal");

const CATEGORIES = [
  { value: "idea", label: "Idea / improvement" },
  { value: "bug", label: "Something's broken" },
  { value: "other", label: "Other" },
];

/**
 * Send-feedback modal — a lightweight suggestion box. Entries are saved
 * on the platform (Platform → Feedback) for staff to review in batches;
 * nothing is emailed on submit, so users can be candid without feeling
 * like they're filing a ticket at someone.
 */
export const FeedbackModal = ({ onClose }) => {
  const api = useAPI();
  const toast = useToast();
  const [category, setCategory] = useState("idea");
  const [message, setMessage] = useState("");
  const [busy, setBusy] = useState(false);

  const submit = async (e) => {
    e?.preventDefault?.();
    if (!message.trim() || busy) return;
    setBusy(true);
    const res = await api.callApi("feedbackCreate", {
      body: {
        category,
        message: message.trim(),
        page: window.location.pathname,
      },
      suppressError: true,
    });
    setBusy(false);
    if (!res || res.error) {
      toast.show({ message: res?.error ?? "Couldn't send feedback, try again.", type: "error" });
      return;
    }
    toast.show({ message: "Thank you! Your feedback was saved." });
    onClose?.();
  };

  return (
    <Modal visible onHide={onClose} title="Send feedback" style={{ width: 480 }}>
      <form className={block.toClassName()} onSubmit={submit}>
        <select
          className={block.elem("category").toClassName()}
          value={category}
          onChange={(e) => setCategory(e.target.value)}
          aria-label="Feedback category"
        >
          {CATEGORIES.map((c) => (
            <option key={c.value} value={c.value}>
              {c.label}
            </option>
          ))}
        </select>
        <textarea
          className={block.elem("message").toClassName()}
          rows={5}
          maxLength={4000}
          placeholder="What's working, what's missing, what should we improve?"
          value={message}
          onChange={(e) => setMessage(e.target.value)}
          autoFocus
        />
        <p className={block.elem("hint").toClassName()}>
          Goes straight to the platform team — reviewed in batches to shape what gets built next.
        </p>
        <div className={block.elem("actions").toClassName()}>
          <Button type="button" look="outlined" size="medium" onClick={onClose} disabled={busy}>
            Cancel
          </Button>
          <Button type="submit" variant="primary" size="medium" disabled={busy || !message.trim()} waiting={busy}>
            {busy ? "Sending…" : "Send feedback"}
          </Button>
        </div>
      </form>
    </Modal>
  );
};
